import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Bell, Send, Trash2, AlertTriangle, CheckCheck } from "lucide-react";
import { toast } from "sonner";

const ROLE_OPTIONS = [
  ["TEMPLE_ADMIN", "Temple Admins"], ["SUPERVISOR", "Supervisors"], ["COUNTER_USER", "Counter Users"], ["VERIFICATION_USER", "Verification Users"],
];
const timeAgo = (iso) => {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  if (m < 1440) return `${Math.round(m / 60)} h ago`;
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

// Bell in the header: notifications sent by Super Admin / Temple Admin, with an unread count.
export default function NotificationBell() {
  const { user } = useAuth();
  const admin = ["SUPER_ADMIN", "TEMPLE_ADMIN"].includes(user.role);
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const seen = useRef(null); // ids already known, so only newly arrived important ones pop up

  const load = useCallback(() => {
    api.get("/notifications").then((r) => {
      setData(r.data);
      const ids = new Set(r.data.items.map((n) => n.id));
      if (seen.current) {
        r.data.items.filter((n) => !seen.current.has(n.id) && !n.read && n.important)
          .forEach((n) => toast.warning(n.title, { description: n.message, duration: 15000 }));
      }
      seen.current = ids;
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const markAll = () => api.post("/notifications/read", {}).then(load).catch(() => {});
  const onOpen = (o) => {
    setOpen(o);
    if (!o && data.unread) markAll(); // closing the list marks what was shown as read
  };
  const remove = async (n) => {
    if (!window.confirm(`Delete notification "${n.title}" for everyone?`)) return;
    try { await api.delete(`/notifications/${n.id}`); toast.success("Notification deleted"); load(); } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <>
      <Popover open={open} onOpenChange={onOpen}>
        <PopoverTrigger asChild>
          <button type="button" className="relative inline-flex h-10 w-10 items-center justify-center rounded-md text-gold-300 hover:text-white hover:bg-white/10" data-testid="nav-notifications-btn" title="Notifications">
            <Bell className="w-5 h-5" />
            {data.unread > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-red-600 px-1 text-[10px] font-bold leading-[18px] text-white text-center" data-testid="notif-count">
                {data.unread > 99 ? "99+" : data.unread}
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-[360px] max-w-[92vw] p-0" data-testid="notif-panel">
          <div className="flex items-center justify-between border-b border-[#E5D9C5] px-3 py-2">
            <p className="font-heading font-bold text-maroon-900">Notifications</p>
            <div className="flex gap-1">
              {data.unread > 0 && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={markAll}><CheckCheck className="w-3.5 h-3.5 mr-1" /> Mark all read</Button>}
              {admin && <Button size="sm" className="h-7 text-xs bg-maroon-700 hover:bg-maroon-800 text-white" onClick={() => { setOpen(false); setSendOpen(true); }} data-testid="notif-send-open"><Send className="w-3.5 h-3.5 mr-1" /> Send</Button>}
            </div>
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {data.items.length === 0 && <p className="px-3 py-8 text-center text-sm text-slate-400">No notifications</p>}
            {data.items.map((n) => (
              <div key={n.id} className={`border-b border-[#F5EDE0] px-3 py-2.5 ${n.read ? "" : "bg-gold-50/70"}`} data-testid="notif-item">
                <div className="flex items-start gap-2">
                  {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-red-600" />}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-maroon-900 flex items-center gap-1">
                      {n.important && <AlertTriangle className="w-4 h-4 text-orange-600 shrink-0" />}{n.title}
                    </p>
                    <p className="text-sm text-slate-700 whitespace-pre-line break-words">{n.message}</p>
                    <p className="mt-1 text-[11px] text-slate-500">
                      {n.created_by_name} · {timeAgo(n.created_at)}
                      {user.role === "SUPER_ADMIN" && ` · ${n.temple_id || "All temples"}`}
                      {admin && n.roles?.length ? ` · to ${n.roles.map((r) => ROLE_OPTIONS.find((o) => o[0] === r)?.[1] || r).join(", ")}` : ""}
                    </p>
                  </div>
                  {n.can_delete && <button type="button" title="Delete" onClick={() => remove(n)} className="text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>}
                </div>
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      {admin && <SendDialog open={sendOpen} onOpenChange={setSendOpen} onSent={load} />}
    </>
  );
}

function SendDialog({ open, onOpenChange, onSent }) {
  const { user } = useAuth();
  const [temples, setTemples] = useState([]);
  const [f, setF] = useState({ title: "", message: "", temple_id: "", roles: [], important: false });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open && user.role === "SUPER_ADMIN" && !temples.length) api.get("/meta/form-data").then((r) => setTemples(r.data.temples || [])).catch(() => {});
  }, [open]); // eslint-disable-line
  const toggleRole = (r) => setF((x) => ({ ...x, roles: x.roles.includes(r) ? x.roles.filter((y) => y !== r) : [...x.roles, r] }));
  const send = async () => {
    if (!f.title.trim() || !f.message.trim()) { toast.error("Enter a title and message"); return; }
    setBusy(true);
    try {
      await api.post("/notifications", f);
      toast.success("Notification sent");
      setF({ title: "", message: "", temple_id: "", roles: [], important: false });
      onOpenChange(false);
      onSent();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="notif-send-dialog">
        <DialogHeader><DialogTitle className="font-heading text-maroon-900">Send Notification</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Title *</Label><Input value={f.title} maxLength={120} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. Counter timings changed" data-testid="notif-title" /></div>
          <div><Label className="text-xs">Message *</Label><Textarea rows={4} value={f.message} maxLength={2000} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder="Type the message for the users" data-testid="notif-message" /></div>
          {user.role === "SUPER_ADMIN" ? (
            <div><Label className="text-xs">Temple</Label>
              <select value={f.temple_id} onChange={(e) => setF({ ...f, temple_id: e.target.value })} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm" data-testid="notif-temple">
                <option value="">All temples</option>
                {temples.map((t) => <option key={t.temple_id} value={t.temple_id}>{t.temple_id} · {t.name}</option>)}
              </select></div>
          ) : <p className="text-xs text-slate-500">Sent to the users of your temple.</p>}
          <div>
            <Label className="text-xs">Send to (none ticked = all users)</Label>
            <div className="mt-1 grid grid-cols-2 gap-1.5">
              {ROLE_OPTIONS.map(([r, label]) => (
                <label key={r} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.roles.includes(r)} onChange={() => toggleRole(r)} data-testid={`notif-role-${r}`} />{label}</label>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.important} onChange={(e) => setF({ ...f, important: e.target.checked })} data-testid="notif-important" />
            Important (pops up on users' screens)</label>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={send} disabled={busy} className="bg-maroon-700 hover:bg-maroon-800 text-white" data-testid="notif-send"><Send className="w-4 h-4 mr-1" /> Send</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
